import React from 'react'
import {styled} from 'app/styles'

const START = 'flex-start'
const END = 'flex-end'
const MID = 'center'
const topBot = p => (p.top ? START : p.bottom ? END : MID)
const leftRight = p => (p.left ? START : p.right ? END : MID)

export interface BoxProps {
	children?: React.ReactNode
	absolute?: boolean
	bottom?: boolean
	column?: boolean
	columnReverse?: boolean
	className?: string
	fitChildren?: boolean
	flex?: string
	gap?: boolean | string
	height?: number | string
	inline?: boolean
	inverse?: boolean
	left?: boolean
	margin?: string | number
	maxHeight?: string | number
	maxWidth?: string | number
	minHeight?: string | number
	minWidth?: string | number
	size?: string | number
	padding?: boolean | string | number
	posBottom?: number | string
	posLeft?: number | string
	posTop?: number | string
	posRight?: number | string
	order?: number
	relative?: boolean
	right?: boolean
	spaceAround?: boolean
	spaceBetween?: boolean
	spaceEvenly?: boolean
	stretch?: boolean
	style?: React.CSSProperties
	top?: boolean
	width?: string | number
	wrap?: boolean
	// Cap-specific
	active?: boolean
	cut?: boolean
	cutSize?: number
	leftCut?: boolean
	mobile?: boolean
}

const Box = styled(
	React.forwardRef<
		HTMLDivElement,
		BoxProps & React.DOMAttributes<HTMLDivElement>
	>(
		(
			{
				absolute,
				active,
				bottom,
				column,
				columnReverse,
				cut,
				cutSize,
				fitChildren,
				flex,
				gap,
				height,
				inline,
				left,
				leftCut,
				maxHeight,
				maxWidth,
				minHeight,
				minWidth,
				mobile,
				padding,
				posBottom,
				posLeft,
				posRight,
				posTop,
				order,
				relative,
				right,
				spaceAround,
				spaceBetween,
				spaceEvenly,
				stretch,
				top,
				width,
				wrap,
				...props
			},
			ref
		) => <div {...props} ref={ref} />
	)
)`
	${p => {
		const gap = p.gap === true ? p.theme.gap : p.gap
		const styles = {
			position: p.relative ? 'relative' : p.absolute ? 'absolute' : undefined,
			top: p.posTop,
			left: p.posLeft,
			bottom: p.posBottom,
			right: p.posRight,

			display: p.inline ? 'inline-flex' : 'flex',

			// Size
			padding: p.padding === true ? gap : p.padding,
			margin: p.wrap && gap ? `-calc(${gap}/2)` : p.margin,
			width:
				p.width ||
				p.size ||
				(!(p.fitChildren || p.inline) ? `100%` : undefined),
			height: p.height || p.size,
			minWidth: p.minWidth,
			maxWidth: p.maxWidth,
			minHeight: p.minHeight,
			maxHeight: p.maxHeight,

			// Children layout
			flexWrap: p.wrap ? 'wrap' : undefined,
			flexDirection:
				p.column && !p.inverse
					? 'column'
					: p.columnReverse || (p.column && p.inverse)
						? 'column-reverse'
						: p.inverse && !p.column
							? 'row-reverse'
							: undefined,
			justifyContent: p.spaceBetween
				? 'space-between'
				: p.spaceAround
					? 'space-around'
					: p.spaceEvenly
						? 'space-evenly'
						: p.column
							? topBot(p)
							: leftRight(p),
			alignItems: p.stretch ? 'stretch' : p.column ? leftRight(p) : topBot(p),
			flex: p.flex,
			order: p.order,

			// Gap between children
			// For flex we use negative + positive margin
			'& > *:not(:last-child)': !p.wrap &&
				gap && {
					[`margin-${
						p.column
							? p.inverse
								? 'top'
								: 'bottom'
							: p.inverse
								? 'left'
								: 'right'
					}`]: gap,
				},
			'& > *': p.wrap &&
				gap && {
					margin: `calc(${gap}/2)`,
				},
		} as const
		return styles
	}};
`

/**
 * A Box component that uses Tailwind classes for layout, flex, spacing, and
 * positioning. For color props, uses styled-components' css function to
 * generate a class (temporary solution).
 */
export const TWBox = React.forwardRef<HTMLDivElement, BoxProps>(function TWBox(
	{
		children,
		absolute,
		bottom,
		className,
		column,
		columnReverse,
		fitChildren,
		flex,
		gap,
		height,
		inline,
		inverse,
		left,
		margin,
		maxHeight,
		maxWidth,
		minHeight,
		minWidth,
		padding,
		posBottom,
		posLeft,
		posRight,
		posTop,
		order,
		relative,
		right,
		size,
		spaceAround,
		spaceBetween,
		spaceEvenly,
		stretch,
		top,
		width,
		wrap,
		// Cap-specific
		active,
		cut,
		cutSize,
		leftCut,
		mobile,
		style: givenStyle,
		...props
	},
	ref
) {
	// Tailwind class mapping helpers
	const classes: string[] = []
	// Custom inline styles for arbitrary values
	const style: React.CSSProperties = givenStyle || {}

	// Positioning
	if (absolute) classes.push('absolute')
	else if (relative) classes.push('relative')
	if (posTop !== undefined) style.top = posTop
	if (posLeft !== undefined) style.left = posLeft
	if (posBottom !== undefined) style.bottom = posBottom
	if (posRight !== undefined) style.right = posRight

	// Display
	classes.push(inline ? 'inline-flex' : 'flex')

	// Flex
	if (flex) {
		if (typeof flex === 'string' && flex.startsWith('flex-')) classes.push(flex)
		else style.flex = flex
	}

	// Fit children
	if (fitChildren) classes.push('w-fit')

	// Flex direction
	if (column && !columnReverse && !inverse) classes.push('flex-col')
	else if (columnReverse || (column && inverse))
		classes.push('flex-col-reverse')
	else if (inverse && !column) classes.push('flex-row-reverse')

	// Flex wrap
	if (wrap) classes.push('flex-wrap')

	// Justify content
	if (spaceBetween) classes.push('justify-between')
	else if (spaceAround) classes.push('justify-around')
	else if (spaceEvenly) classes.push('justify-evenly')
	else if (column) {
		if (top) classes.push('justify-start')
		else if (bottom) classes.push('justify-end')
		else classes.push('justify-center')
	} else {
		if (left) classes.push('justify-start')
		else if (right) classes.push('justify-end')
		else classes.push('justify-center')
	}

	// Align items
	if (stretch) classes.push('items-stretch')
	else if (column) {
		if (left) classes.push('items-start')
		else if (right) classes.push('items-end')
		else classes.push('items-center')
	} else {
		if (top) classes.push('items-start')
		else if (bottom) classes.push('items-end')
		else classes.push('items-center')
	}

	// Gap
	if (gap) {
		if (gap === true)
			classes.push('gap-4') // default gap, adjust as needed
		else if (typeof gap === 'string' && gap.startsWith('gap-'))
			classes.push(gap)
		else {
			style.gap = gap
		}
	}

	// Padding
	if (padding) {
		if (padding === true) classes.push('p-4')
		else if (typeof padding === 'string' && padding.startsWith('p-'))
			classes.push(padding)
		else style.padding = padding
	}

	// Margin
	if (margin) {
		if (typeof margin === 'string' && margin.startsWith('m-'))
			classes.push(margin)
		else style.margin = margin
	}

	// Width/height/size
	if (size) {
		width = size
		height = size
	}
	if (width) {
		if (width === 'full' || width === '100%' || width === 1)
			classes.push('w-full')
		else if (typeof width === 'string' && width.startsWith('w-'))
			classes.push(width)
		else style.width = width
	} else if (!(fitChildren || inline)) classes.push('w-full')
	if (height) {
		if (height === 'full' || height === '100%' || height === 1)
			classes.push('h-full')
		else if (typeof height === 'string' && height.startsWith('h-'))
			classes.push(height)
		else style.height = height
	}

	// Min/max width/height
	if (minWidth) {
		if (typeof minWidth === 'string' && minWidth.startsWith('min-w-'))
			classes.push(minWidth)
		else style.minWidth = minWidth
	}
	if (maxWidth) {
		if (typeof maxWidth === 'string' && maxWidth.startsWith('max-w-'))
			classes.push(maxWidth)
		else style.maxWidth = maxWidth
	}
	if (minHeight) {
		if (typeof minHeight === 'string' && minHeight.startsWith('min-h-'))
			classes.push(minHeight)
		else style.minHeight = minHeight
	}
	if (maxHeight) {
		if (typeof maxHeight === 'string' && maxHeight.startsWith('max-h-'))
			classes.push(maxHeight)
		else style.maxHeight = maxHeight
	}

	// Order
	if (typeof order === 'number') classes.push(`order-${order}`)

	if (className) classes.push(className)
	const finalClassName = classes.join(' ')
	return (
		<div ref={ref} className={finalClassName} style={style} {...props}>
			{children}
		</div>
	)
})

export default Box
